import { useEffect, useMemo, useState } from 'react';
import { Button, Grid, Skeleton, Space } from '@arco-design/web-react';
import { IconPlus, IconUserAdd } from '@arco-design/web-react/icon';
import ReactECharts from 'echarts-for-react';
import { useNavigate } from 'react-router-dom';
import { PageHeader, StatCard } from '@/components/ui';
import {
  dashboardApi,
  type DashboardSpendPoint,
  type DashboardSummary,
} from '@/api';
import { centsToDollars, formatMoney } from '@/utils/money';
import { useAuthStore } from '@/store/authStore';

const { Row, Col } = Grid;

export default function DashboardView() {
  const navigate = useNavigate();
  const patchProfile = useAuthStore((s) => s.patchProfile);
  const [summary, setSummary] = useState<DashboardSummary | null>(null);
  const [trend, setTrend] = useState<DashboardSpendPoint[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    Promise.all([dashboardApi.summary(), dashboardApi.spendTrend(30)])
      .then(([s, t]) => {
        if (cancelled) return;
        setSummary(s);
        setTrend(t);
        // 与顶部「主账户余额」对齐：用实时 KPI 回写 auth store
        patchProfile({ walletBalanceCents: s.walletBalanceCents });
      })
      .catch(() => {
        /* 拦截器已 toast */
      })
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [patchProfile]);

  const chartOption = useMemo(
    () => ({
      grid: { left: 40, right: 16, top: 24, bottom: 32 },
      xAxis: {
        type: 'category',
        data: trend.map((p) => p.date),
        axisLine: { lineStyle: { color: '#E5E6EB' } },
        axisLabel: { color: '#86909C' },
      },
      yAxis: {
        type: 'value',
        axisLabel: {
          color: '#86909C',
          formatter: (v: number) => `$${(v / 100).toFixed(0)}`,
        },
        splitLine: { lineStyle: { color: '#F2F3F5' } },
      },
      tooltip: {
        trigger: 'axis',
        valueFormatter: (v: number) => formatMoney(v),
      },
      series: [
        {
          type: 'line',
          smooth: true,
          showSymbol: false,
          lineStyle: { color: '#165DFF', width: 2 },
          areaStyle: {
            color: {
              type: 'linear',
              x: 0,
              y: 0,
              x2: 0,
              y2: 1,
              colorStops: [
                { offset: 0, color: 'rgba(22,93,255,0.25)' },
                { offset: 1, color: 'rgba(22,93,255,0)' },
              ],
            },
          },
          data: trend.map((p) => p.amountCents),
        },
      ],
    }),
    [trend],
  );

  return (
    <div className="up-page">
      <PageHeader
        title="全局面板"
        subtitle="实时呈现账户资产、有效卡片与近期消费趋势"
        extra={
          <Space>
            <Button type="primary" icon={<IconPlus />} onClick={() => navigate('/cards')}>
              一键申请新卡
            </Button>
            <Button icon={<IconUserAdd />} onClick={() => navigate('/billing-templates')}>
              新增账单持卡人
            </Button>
          </Space>
        }
      />

      {loading || !summary ? (
        <Skeleton animation text={{ rows: 4 }} />
      ) : (
        <>
          <Row gutter={16}>
            <Col span={6}>
              <StatCard
                label="账户可用余额"
                value={formatMoney(summary.walletBalanceCents)}
                delta={`折合 ${centsToDollars(summary.walletBalanceCents).toFixed(2)} USD`}
              />
            </Col>
            <Col span={6}>
              <StatCard label="有效虚拟卡数量" value={summary.activeCardCount} delta="生命周期为 ACTIVE" />
            </Col>
            <Col span={6}>
              <StatCard
                label="本月已消费"
                value={formatMoney(summary.monthSpentCents)}
                delta="含成功结算 + 退款抵扣"
              />
            </Col>
            <Col span={6}>
              <StatCard
                label="本月成功率"
                value={`${(summary.monthSuccessRate * 100).toFixed(1)}%`}
                delta="成功交易占比"
              />
            </Col>
          </Row>

          <div className="up-section-card" style={{ marginTop: 16 }}>
            <div className="up-page__title" style={{ fontSize: 16 }}>
              近 30 日消费趋势
            </div>
            <ReactECharts option={chartOption} style={{ height: 320 }} />
          </div>
        </>
      )}
    </div>
  );
}
